import React, { useEffect, useState, createContext, useContext } from 'react';
import { BiUser } from 'react-icons/bi';
import { MdPayment, MdDashboard } from 'react-icons/md';
import { FaSpinner } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { rootUrl } from '../helpers';
import { useAuth } from '../context/auth-context';

import Payment from '../pages/mobile/payment';
import ProfilePage from '../pages/mobile/profile';
import Dash from '../pages/mobile/dash';

// Create context for mobile layout
const MobileContext = createContext();

// Hook to use mobile context
export const useMobileContext = () => {
  const context = useContext(MobileContext);
  if (!context) {
    throw new Error('useMobileContext must be used within MobileContextProvider');
  }
  return context;
};

const PhoneLayout = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [allData, setAllData] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const auth = useAuth();

  const getGen = async () => {
    try {
      const response = await axios.get(rootUrl(`/getGeneral/${auth.user?.id}`));
      if (response.data.status == 200) {
        setAllData(response.data.data);
        return true;
      }
      return false;
    } catch (e) {
      console.error("Error fetching general data:", e);
      return false;
    }
  };

  const fetchReceipts = async () => {
    try {
      const response = await axios.get(rootUrl(`/receipts/${auth.user?.id}`));
      if (response.data.status == 200) {
        setReceipts(response.data.data);
        return true;
      }
      return false;
    } catch (e) {
      console.error("Error fetching receipts:", e);
      return false;
    }
  };

  // Process receipts into grouped data
  const processReceipts = () => {
    if (!allData || !receipts || receipts.length === 0) {
      setGroupedData([]);
      return;
    }

    const grouped = Object.values(
      receipts.reduce((acc, item) => {
        if (!acc[item.voucher]) {
          acc[item.voucher] = {
            voucher: item.voucher,
            date: item.date,
            paye: allData.suppliers?.find(a => a.id == item.paye)?.name || 'Unknown',
            type: item.type,
            cid: item.cid,
            bank: item.bank,
            sof: item.sof,
            cheque: item.cheque,
            updatedBy: item.updatedBy,
            proj_id: item.proj_id,
            status: item.status,
            recon: item.recon,
            transactions: []
          };
        }
        acc[item.voucher].transactions.push({
          amount: item.amount,
          coa_id: allData.budget?.find(a => a.id == item.coa_id)?.coa || 'Unknown',
          description: item.description
        });
        return acc;
      }, {})
    );
    setGroupedData(grouped);
  };

  // Initialize tabs after data is loaded
  const initializeTabs = () => {
    if (!allData || !allData.users) return;

    const newTabs = [
      {
        id: 0,
        label: "Dashboard",
        icon: <MdDashboard className="w-6 h-6" />,
        content: (
          <Dash
            allData={allData}
            user={allData.users.find(a => a.id == auth.user?.id)}
            receipts={groupedData}
          />
        ),
      },
      {
        id: 1,
        label: "Payment",
        icon: <MdPayment className="w-6 h-6" />,
        content: <Payment />,
      },
      {
        id: 2,
        label: "Profile",
        icon: <BiUser className="w-6 h-6" />,
        content: <ProfilePage allData={allData} groupedData={groupedData} />,
      },
    ];
    setTabs(newTabs);
  };

  // Load all data and wait for completion
  const loadAllData = async () => {
    setIsLoading(true);
    setDataLoaded(false);

    try {
      const [genDataSuccess, receiptsSuccess] = await Promise.all([
        getGen(),
        fetchReceipts()
      ]);

      if (genDataSuccess && receiptsSuccess) {
        setDataLoaded(true);
      } else {
        console.error("Failed to load required data");
        setTimeout(() => {
          loadAllData();
        }, 2000);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      setTimeout(() => {
        loadAllData();
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (auth.user?.id) {
      loadAllData();
    }
  }, [auth.user?.id]);

  useEffect(() => {
    if (allData && receipts && dataLoaded) {
      processReceipts();
    }
  }, [allData, receipts, dataLoaded]);

  useEffect(() => {
    if (allData && groupedData && dataLoaded) {
      initializeTabs();
    }
  }, [allData, groupedData, dataLoaded]);

  if (isLoading || !dataLoaded || !allData || !receipts || tabs.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center p-8">
          <div className="relative">
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 animate-pulse"></div>
              <div className="absolute inset-2 rounded-full bg-white flex items-center justify-center">
                <FaSpinner className="text-blue-600 text-2xl animate-spin" />
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-gray-800">Loading your dashboard</h2>
              <p className="text-gray-600">Please wait while we prepare your data</p>
              <div className="w-32 h-1 bg-gray-200 rounded-full mx-auto mt-4">
                <div className="h-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /** Refresh without full-screen loader (avoids unmounting Payment during SchoolPay sync). */
  const refreshAllData = async () => {
    try {
      const [genOk, recOk] = await Promise.all([getGen(), fetchReceipts()]);
      if (genOk && recOk) {
        setDataLoaded(true);
      }
    } catch (error) {
      console.error("Error refreshing mobile data:", error);
    }
  };

  return (
    <MobileContext.Provider value={{ allData, setAllData, receipts, groupedData, activeTab, refreshAllData }}>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        
        {/* Gradient Top Bar */}
        <div className="h-14 fixed top-0 left-0 right-0 flex items-center px-4 bg-white shadow-md z-50 shadow-lg">
          <h1 className="text-black text-lg font-bold truncate">
            {allData?.council?.[0]?.name || 'Loading...'}
          </h1>
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto pb-24 pt-16 h-screen scrollbar-none">
          <div className="px-4 py-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {tabs[activeTab]?.content}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>

        {/* Modern Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 z-50 pb-safe">
          <div className="absolute inset-0 bg-white/80 backdrop-blur-xl border-t border-white/20"></div>
          <div className="relative px-6 py-3">
            <div className="flex justify-around items-center">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="group relative flex flex-col items-center p-3 rounded-2xl"
                >
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeBackground"
                      className="absolute inset-0 bg-blue-600 rounded-2xl shadow-lg"
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    />
                  )}
                  <motion.div
                    className="relative z-10 mb-1"
                    animate={activeTab === tab.id ? { y: -2, scale: 1.2 } : { y: 0, scale: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  >
                    {React.cloneElement(tab.icon, {
                      className: `w-6 h-6 transition-colors duration-300 ${
                        activeTab === tab.id ? "text-white" : "text-gray-500 group-hover:text-gray-700"
                      }`
                    })}
                  </motion.div>
                  <span
                    className={`text-xs font-semibold transition-all duration-300 relative z-10 ${
                      activeTab === tab.id ? "text-white" : "text-gray-500 group-hover:text-gray-700"
                    }`}
                  >
                    {tab.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </nav>
      </div>
    </MobileContext.Provider>
  );
};

export default PhoneLayout;
